/* =========================================================
   PH PERFUMERÍA — CONTACTO
   Versión: 1.1
   Archivo: assets/ph-contact.js

   Funciones:
   - Animaciones suaves al entrar en pantalla
   - FAQ con una sola pregunta abierta
   - Selección automática del motivo desde las tarjetas
   - Campo de número de pedido contextual
   - Scroll suave para anclas internas
   - Carrusel móvil en “¿Cómo podemos ayudarte?”
   - Flechas dinámicas sin bloquear el scroll vertical
   - Compatible con el editor de temas de Shopify
   ========================================================= */

(() => {
  'use strict';

  const SELECTORS = {
    root: '[data-ph-contact]',
    reveal: '[data-ph-contact-reveal]',
    faq: '.ph-contact__faq-item',
    reasonSelect: '[data-ph-contact-reason]',
    reasonLink: '[data-ph-contact-reason-link]',
    orderField: '[data-ph-contact-order-field]',
    orderInput: '[data-ph-contact-order-field] input',
    helpTrack: '[data-ph-contact-help-track]',
    helpCard: '.ph-contact__help-card'
  };

  const CLASSES = {
    ready: 'ph-contact--ready',
    visible: 'ph-contact-reveal--visible',
    orderVisible: 'ph-contact__field--order-visible',

    carousel: 'ph-contact__help-carousel',
    controls: 'ph-contact__help-controls',
    arrow: 'ph-contact__help-arrow',
    arrowPrevious: 'ph-contact__help-arrow--previous',
    arrowNext: 'ph-contact__help-arrow--next',
    arrowHidden: 'ph-contact__help-arrow--hidden',
    carouselStatic: 'ph-contact__help-carousel--static'
  };

  const MOBILE_QUERY = '(max-width: 749px)';

  const ORDER_REASONS = [
    'Duda sobre un pedido',
    'Envíos',
    'Cambio o incidencia'
  ];

  const prefersReducedMotion = () => {
    return window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
  };

  const isMobileViewport = () => {
    return window.matchMedia(MOBILE_QUERY).matches;
  };

  /* =======================================================
     REVEAL
     ======================================================= */

  const revealAll = (root) => {
    root
      .querySelectorAll(SELECTORS.reveal)
      .forEach((element) => {
        element.classList.add(CLASSES.visible);
      });
  };

  const initRevealAnimations = (root) => {
    const elements = Array.from(
      root.querySelectorAll(SELECTORS.reveal)
    );

    if (!elements.length) return;

    if (
      prefersReducedMotion() ||
      !('IntersectionObserver' in window)
    ) {
      revealAll(root);
      return;
    }

    elements.forEach((element, index) => {
      const delay = Math.min(
        (index % 5) * 60,
        240
      );

      element.style.setProperty(
        '--ph-contact-reveal-delay',
        `${delay}ms`
      );
    });

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add(
            CLASSES.visible
          );

          currentObserver.unobserve(
            entry.target
          );
        });
      },
      {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12
      }
    );

    elements.forEach((element) => {
      observer.observe(element);
    });
  };

  /* =======================================================
     FAQ
     ======================================================= */

  const initFaq = (root) => {
    const items = Array.from(
      root.querySelectorAll(SELECTORS.faq)
    );

    if (!items.length) return;

    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;

        items.forEach((otherItem) => {
          if (otherItem !== item) {
            otherItem.removeAttribute('open');
          }
        });
      });
    });
  };

  /* =======================================================
     CAMPO DE NÚMERO DE PEDIDO
     ======================================================= */

  const initReasonField = (root) => {
    const reasonSelect = root.querySelector(
      SELECTORS.reasonSelect
    );

    const orderField = root.querySelector(
      SELECTORS.orderField
    );

    const orderInput = root.querySelector(
      SELECTORS.orderInput
    );

    if (!reasonSelect || !orderField) {
      return null;
    }

    const updateOrderField = () => {
      const shouldShow = ORDER_REASONS.includes(
        reasonSelect.value
      );

      orderField.classList.toggle(
        CLASSES.orderVisible,
        shouldShow
      );

      orderField.hidden = !shouldShow;

      if (orderInput) {
        orderInput.disabled = !shouldShow;

        if (!shouldShow) {
          orderInput.value = '';
        }
      }
    };

    reasonSelect.addEventListener(
      'change',
      updateOrderField
    );

    updateOrderField();

    return {
      reasonSelect,
      updateOrderField
    };
  };

  /* =======================================================
     TARJETAS DE AYUDA
     ======================================================= */

  const initReasonLinks = (
    root,
    reasonFieldController
  ) => {
    const links = Array.from(
      root.querySelectorAll(
        SELECTORS.reasonLink
      )
    );

    if (
      !links.length ||
      !reasonFieldController
    ) {
      return;
    }

    const {
      reasonSelect,
      updateOrderField
    } = reasonFieldController;

    links.forEach((link) => {
      link.addEventListener('click', () => {
        const reason =
          link.dataset.phContactReasonLink;

        if (!reason) return;

        reasonSelect.value = reason;
        updateOrderField();

        /*
          No usamos preventDefault aquí.
          El enlace conserva su navegación natural
          hacia el formulario.
        */
      });
    });
  };

  /* =======================================================
     SCROLL SUAVE PARA ANCLAS INTERNAS
     ======================================================= */

  const initSmoothAnchors = (root) => {
    const links = Array.from(
      root.querySelectorAll('a[href^="#"]')
    );

    if (!links.length) return;

    links.forEach((link) => {
      link.addEventListener(
        'click',
        (event) => {
          const href =
            link.getAttribute('href');

          if (!href || href === '#') return;

          const target =
            document.querySelector(href);

          if (!target) return;

          event.preventDefault();

          target.scrollIntoView({
            behavior: prefersReducedMotion()
              ? 'auto'
              : 'smooth',
            block: 'start'
          });

          if (
            window.history &&
            typeof window.history
              .replaceState === 'function'
          ) {
            window.history.replaceState(
              null,
              '',
              href
            );
          }
        }
      );
    });
  };

  /* =======================================================
     MENSAJE DE ÉXITO
     ======================================================= */

  const focusFormStatus = (root) => {
    const status = root.querySelector(
      '.ph-contact__form-status'
    );

    if (!status) return;

    window.setTimeout(() => {
      status.focus({
        preventScroll: true
      });
    }, 120);
  };

  /* =======================================================
     CREAR FLECHAS DEL CARRUSEL
     ======================================================= */

  const createCarouselArrow = (
    direction
  ) => {
    const button =
      document.createElement('button');

    const isPrevious =
      direction === 'previous';

    button.type = 'button';

    button.className = [
      CLASSES.arrow,
      isPrevious
        ? CLASSES.arrowPrevious
        : CLASSES.arrowNext
    ].join(' ');

    button.setAttribute(
      'aria-label',
      isPrevious
        ? 'Volver a la primera opción'
        : 'Ver la siguiente opción'
    );

    button.innerHTML = `
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="${
            isPrevious
              ? 'M15 5l-7 7 7 7'
              : 'M9 5l7 7-7 7'
          }"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;

    return button;
  };

  /* =======================================================
     CARRUSEL MÓVIL DE AYUDA
     ======================================================= */

  const initHelpCarousel = (root) => {
    const track = root.querySelector(
      SELECTORS.helpTrack
    );

    if (
      !track ||
      track.dataset.phContactCarouselReady ===
        'true'
    ) {
      return;
    }

    track.dataset.phContactCarouselReady =
      'true';

    const carousel =
      document.createElement('div');

    carousel.className = CLASSES.carousel;

    const controls =
      document.createElement('div');

    controls.className = CLASSES.controls;

    const previousButton =
      createCarouselArrow('previous');

    const nextButton =
      createCarouselArrow('next');

    track.parentNode.insertBefore(
      carousel,
      track
    );

    carousel.appendChild(track);
    carousel.appendChild(controls);

    controls.appendChild(previousButton);
    controls.appendChild(nextButton);

    const getTrackState = () => {
      const maxScrollLeft = Math.max(
        0,
        track.scrollWidth -
          track.clientWidth
      );

      /*
        Tolerancia para scroll-snap y
        valores decimales del navegador.
      */
      const tolerance = 10;

      return {
        maxScrollLeft,
        hasOverflow:
          maxScrollLeft > tolerance,
        atBeginning:
          track.scrollLeft <= tolerance,
        atEnd:
          track.scrollLeft >=
          maxScrollLeft - tolerance
      };
    };

    const getScrollAmount = () => {
      const firstCard =
        track.querySelector(
          SELECTORS.helpCard
        );

      if (!firstCard) {
        return track.clientWidth * 0.85;
      }

      const styles =
        window.getComputedStyle(track);

      const gap =
        parseFloat(
          styles.columnGap ||
            styles.gap
        ) || 0;

      return (
        firstCard.getBoundingClientRect()
          .width + gap
      );
    };

    const setArrowState = (
      button,
      shouldShow
    ) => {
      button.disabled = !shouldShow;

      button.classList.toggle(
        CLASSES.arrowHidden,
        !shouldShow
      );
    };

    const updateCarouselControls = () => {
      const {
        hasOverflow,
        atBeginning,
        atEnd
      } = getTrackState();

      const shouldActivate =
        isMobileViewport() && hasOverflow;

      carousel.classList.toggle(
        CLASSES.carouselStatic,
        !shouldActivate
      );

      /*
        Escritorio y tablet:
        las tarjetas se muestran normalmente.
        No se necesitan flechas.
      */
      if (!shouldActivate) {
        setArrowState(
          previousButton,
          false
        );

        setArrowState(
          nextButton,
          false
        );

        return;
      }

      /*
        MÓVIL:
        - Durante todo el recorrido solo se
          muestra la flecha derecha.
        - Al llegar al final desaparece la
          derecha y aparece la izquierda.
        */
      const showPrevious = atEnd;
      const showNext = !atEnd;

      setArrowState(
        previousButton,
        showPrevious
      );

      setArrowState(
        nextButton,
        showNext
      );

      /*
        atBeginning se calcula para mantener
        una lectura fiable del estado aunque
        la flecha izquierda no se muestre ahí.
      */
      void atBeginning;
    };

    const scrollForward = () => {
      track.scrollBy({
        left: getScrollAmount(),
        behavior: prefersReducedMotion()
          ? 'auto'
          : 'smooth'
      });
    };

    const returnToBeginning = () => {
      track.scrollTo({
        left: 0,
        behavior: prefersReducedMotion()
          ? 'auto'
          : 'smooth'
      });
    };

    nextButton.addEventListener(
      'click',
      scrollForward
    );

    previousButton.addEventListener(
      'click',
      returnToBeginning
    );

    /*
      Listener pasivo:
      solo observa el desplazamiento.
      No bloquea el gesto vertical.
    */
    track.addEventListener(
      'scroll',
      updateCarouselControls,
      { passive: true }
    );

    /*
      No agregamos listeners para:
      - touchstart
      - touchmove
      - pointermove

      Tampoco usamos preventDefault en gestos.
      Por eso el usuario puede deslizar
      verticalmente sobre las tarjetas.
    */

    const mobileMediaQuery =
      window.matchMedia(MOBILE_QUERY);

    const handleViewportChange = () => {
      updateCarouselControls();
    };

    if (
      typeof mobileMediaQuery
        .addEventListener === 'function'
    ) {
      mobileMediaQuery.addEventListener(
        'change',
        handleViewportChange
      );
    } else {
      mobileMediaQuery.addListener(
        handleViewportChange
      );
    }

    if ('ResizeObserver' in window) {
      const resizeObserver =
        new ResizeObserver(
          updateCarouselControls
        );

      resizeObserver.observe(track);
    } else {
      window.addEventListener(
        'resize',
        updateCarouselControls,
        { passive: true }
      );
    }

    requestAnimationFrame(
      updateCarouselControls
    );

    window.setTimeout(
      updateCarouselControls,
      250
    );
  };

  /* =======================================================
     INICIALIZACIÓN
     ======================================================= */

  const initSection = (root) => {
    if (
      !root ||
      root.dataset.phContactReady ===
        'true'
    ) {
      return;
    }

    root.dataset.phContactReady = 'true';

    root.classList.add(CLASSES.ready);

    initRevealAnimations(root);
    initFaq(root);

    const reasonFieldController =
      initReasonField(root);

    initReasonLinks(
      root,
      reasonFieldController
    );

    initSmoothAnchors(root);
    focusFormStatus(root);
    initHelpCarousel(root);
  };

  const initAll = (
    scope = document
  ) => {
    scope
      .querySelectorAll(SELECTORS.root)
      .forEach(initSection);
  };

  const onReady = () => {
    initAll();
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      onReady,
      { once: true }
    );
  } else {
    onReady();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );
})();